/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { Box } from '@material-ui/core';
import type {
  TicketingInstructionResponseArgs,
  TicketingMethodKey2
} from '@this/src/domain/arrangement/ticketing_instruction';
import TicketingInstruction, {
  convertTicketingInstructionResponseToArgs,
  TicketingMethod,
  TicketingMethod2,
  TicketingMethodKeys2
} from '@this/src/domain/arrangement/ticketing_instruction';
import {
  PreferredTicketingMethodLabel,
  PreferredTicketingMethodQuery,
  PreferredTicketingMethodQueryKeys
} from '@this/src/domain/arrangement/preferred_ticketing_method_query';
import type { AutoReservationStatusQueryKey } from '@this/src/domain/arrangement/auto_reservation_status_query';
import {
  AutoReservationStatusQueryDefault,
  AutoReservationStatusQueryKeys
} from '@this/src/domain/arrangement/auto_reservation_status_query';
import { isMatchingTraceId } from '@this/src/util/string/string.util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/components/shared/ui/data_displays/table';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { Select } from '@this/src/components/shared/ui/inputs/select';
import { Datepicker } from '@this/src/components/shared/ui/inputs/datepicker';
import { Radio } from '@this/src/components/shared/ui/inputs/radio';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher, getParam, HTTPError } from '@this/src/util';
import { TransportElementAttachFileForm } from '@this/components/arrangement/shared/price_change_form/price_change_form_item/transport_element_form/transport_element_attach_file_form';
import Notification from '@this/src/notification';
import TransportElementAttachFile from '@this/src/domain/transport_element_attach_file';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { useConfirmModal } from '@this/shared/ui/feedbacks/modal/confirm_modal';
import moment from 'moment';
import type { TabType } from '../todo_list';
import { SearchArea, SearchBlock, SearchLabel } from '../search_area';
import useOrganizationSelector from '../use_organization_selector';
import LogModal from '../log_modal';
import AutoReservationStatusSelector from '../auto_reservation_status_selector';

type SortKey = 'organization' | 'received_at' | 'started_at';
type SortDirection = 'asc' | 'desc';
type UploadFileData = {
  [traceId: string]: {
    [transportElementId: string]: {
      [travelerIdKey: string]: Array<
        | {
            id: number;
            transport_element_id: number;
            traveler_information_id: string | null;
            trip_id: number;
            upload_file_name: string;
            content_type: string;
            file_path: string;
            upload_file_base64?: never; // 登録済みファイルはBase64を持たない
          }
        | {
            upload_file_name: string;
            content_type: string;
            traveler_information_id: string | null;
            upload_file_base64: string;
            // 新規ファイルには持たない項目
            id?: never;
            transport_element_id?: never;
            trip_id?: never;
            file_path?: never;
          }
      >;
    };
  };
};

type CommittedQuery = {
  page: number;
  organizationIds: string;
  preferredTicketingMethod: keyof typeof PreferredTicketingMethodQuery;
  onlyPremiumSupport: boolean;
  ticketingMethod: TicketingMethodKey2;
  sortKey: SortKey;
  sortDirection: SortDirection;
  startDateFrom: string | null;
  startDateTo: string | null;
  receivedDatetimeFrom: string | null;
  receivedDatetimeTo: string | null;
  tripIdLeadingDigits: number[];
  onlyOthers: boolean;
  autoReservationStatuses: AutoReservationStatusQueryKey[];
};

interface Response {
  ticketing_instructions: TicketingInstructionResponseArgs[];
  total_count: number;
  total_page: number;
}

const tab: TabType = 'TICKETING';

const buildFetchParams = (query: CommittedQuery): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {
    page: query.page,
    organization_ids: query.organizationIds,
    preferred_ticketing_method: query.preferredTicketingMethod,
    only_premium_support: query.onlyPremiumSupport,
    ticketing_method: query.ticketingMethod,
    sort_key: query.sortKey,
    sort_direction: query.sortDirection,
    only_others: query.onlyOthers,
    auto_reservation_statuses: query.autoReservationStatuses.join(',')
  };
  if (query.startDateFrom !== null) params.start_date_from = query.startDateFrom;
  if (query.startDateTo !== null) params.start_date_to = query.startDateTo;
  if (query.receivedDatetimeFrom !== null) params.received_datetime_from = query.receivedDatetimeFrom;
  if (query.receivedDatetimeTo !== null) params.received_datetime_to = query.receivedDatetimeTo;
  if (query.tripIdLeadingDigits.length > 0) params.trip_id_leading_digits = query.tripIdLeadingDigits.join(',');
  return params;
};

// page/ソート以外の検索条件が同一かどうか（processedOrderItemsのクリア要否判定に使用）
const isSameSearchCondition = (a: CommittedQuery, b: CommittedQuery): boolean =>
  _.isEqual(_.omit(a, ['page', 'sortKey', 'sortDirection']), _.omit(b, ['page', 'sortKey', 'sortDirection']));

const getDefaultOnlyPremiumSupport = (): boolean => {
  return getParam('only_premium_support') === 'true';
};

const getDefaultPreferredTicketingMethod = (): keyof typeof PreferredTicketingMethodQuery => {
  return (getParam('preferred_ticketing_method') || 'all') as keyof typeof PreferredTicketingMethodQuery;
};

const getDefaultTicketingMethod = (): TicketingMethodKey2 => {
  return (getParam('ticketing_method') || 'all') as TicketingMethodKey2;
};

const getDefaultSortKey = (): SortKey => {
  return (getParam('sort_key') || 'organization') as SortKey;
};

const getDefaultSortDirection = (): SortDirection => {
  return (getParam('sort_direction') || 'asc') as SortDirection;
};

const getDefaultStartDateFrom = (): string | null => {
  return getParam('start_date_from') || null;
};

const getDefaultStartDateTo = (): string | null => {
  return getParam('start_date_to') || null;
};

const getDefaultReceivedDatetimeFrom = (): string | null => {
  return getParam('received_datetime_from') || null;
};

const getDefaultReceivedDatetimeTo = (): string | null => {
  return getParam('received_datetime_to') || null;
};

const getDefaultTripIdLeadingDigits = (): number[] => {
  const raw = getParam('trip_id_leading_digits');
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map(v => parseInt(v, 10))
    .filter(v => Number.isInteger(v) && v >= 0 && v <= 9);
};

const getDefaultOnlyOthers = (): boolean => {
  const raw = getParam('only_others');
  return raw === undefined || raw === null ? true : raw === 'true';
};

const getDefaultAutoReservationStatuses = (): AutoReservationStatusQueryKey[] => {
  const raw = getParam('auto_reservation_statuses');
  if (raw === undefined) {
    return [...AutoReservationStatusQueryDefault];
  }
  if (raw === null || raw === '') {
    return [];
  }
  const values = String(raw)
    .split(',')
    .map(v => v.trim())
    .filter((v): v is AutoReservationStatusQueryKey =>
      (AutoReservationStatusQueryKeys as readonly string[]).includes(v)
    );
  return values;
};

const Ticketing = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<{ traceId: number; message: string } | null>(null);
  const [ticketingInstcutions, setTicketingInstcutions] = useState<TicketingInstruction[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPage, setTotalPage] = useState(1);
  const [committedQuery, setCommittedQuery] = useState<CommittedQuery | null>(null);
  const [fares, setFares] = useState<Map<number, number>>(new Map());
  const [processedOrderItems, setProcessedOrderItems] = useState<Map<number, 'proceeded' | 'reverted'>>(new Map());

  const [organizationIds, renderOrganizationSelector, setOrganizationIds] = useOrganizationSelector();
  const [preferredTicketingMethod, setPreferredTicketingMethod] = useState<
    keyof typeof PreferredTicketingMethodQuery
  >(getDefaultPreferredTicketingMethod());
  const [onlyPremiumSupport, setOnlyPremiumSupport] = useState<boolean>(getDefaultOnlyPremiumSupport());
  const [ticketingMethod, setTicketingMethod] = useState<TicketingMethodKey2>(getDefaultTicketingMethod());
  const [sortKey, setSortKey] = useState<SortKey>(getDefaultSortKey());
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection());
  const [uploadFileData, setUploadFileData] = useState<UploadFileData>({});
  const [startDateFrom, setStartDateFrom] = useState<string | null>(getDefaultStartDateFrom());
  const [startDateTo, setStartDateTo] = useState<string | null>(getDefaultStartDateTo());
  const [receivedDatetimeFrom, setReceivedDatetimeFrom] = useState<string | null>(
    getDefaultReceivedDatetimeFrom()
  );
  const [receivedDatetimeTo, setReceivedDatetimeTo] = useState<string | null>(getDefaultReceivedDatetimeTo());
  const [tripIdLeadingDigits, setTripIdLeadingDigits] = useState<number[]>(getDefaultTripIdLeadingDigits());
  const [onlyOthers, setOnlyOthers] = useState<boolean>(getDefaultOnlyOthers());
  const [autoReservationStatuses, setAutoReservationStatuses] = useState<AutoReservationStatusQueryKey[]>(
    getDefaultAutoReservationStatuses()
  );

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedTicketingInstruction, setSelectedTicketingInstruction] = useState<TicketingInstruction | null>(
    null
  );
  const [logModalTraceId, setLogModalTraceId] = useState<number | null>(null);
  const { open: openConfirmModal } = useConfirmModal();

  // 直近で取得に成功した検索条件。400エラー時のロールバック先として使う
  const lastSuccessfulQueryRef = useRef<CommittedQuery | null>(null);
  // 古いレスポンスで新しい結果を上書きしないためのリクエストIDガード
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (committedQuery === null) return undefined;
    // 400エラー時の巻き戻しで復元されたクエリ（成功済みクエリと同一参照）は再fetchしない
    if (committedQuery === lastSuccessfulQueryRef.current) return undefined;

    const query = committedQuery;
    const controller = new AbortController();
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setIsLoading(true);
    setError(null);

    const params = buildFetchParams(query);

    Fetcher.get<Response>('/arrangement/ticketings.json', params, undefined, controller.signal)
      .then(res => {
        if (requestId !== requestIdRef.current) return;

        const prevSuccessfulQuery = lastSuccessfulQueryRef.current;
        lastSuccessfulQueryRef.current = query;

        setTicketingInstcutions(
          res.ticketing_instructions.map(
            args => new TicketingInstruction(convertTicketingInstructionResponseToArgs(args))
          )
        );
        setFares(new Map());
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        // ページ送りやソート変更では処理済みマーキングを維持し、検索条件が変わったときだけクリアする
        if (prevSuccessfulQuery === null || !isSameSearchCondition(prevSuccessfulQuery, query)) {
          setProcessedOrderItems(new Map());
        }

        const newUploadFileData = res.ticketing_instructions.reduce((acc, instruction) => {
          const traceId = instruction.trace_id;
          if (!acc[traceId]) {
            acc[traceId] = {};
          }
          instruction.transport_elements?.forEach(element => {
            if (!acc[traceId][element.id]) acc[traceId][element.id] = {};

            element.transport_element_attach_files?.forEach(file => {
              const travelerId = file.traveler_information_id?.toString() || 'null';
              if (!acc[traceId][element.id][travelerId]) {
                acc[traceId][element.id][travelerId] = [];
              }
              acc[traceId][element.id][travelerId].push({
                id: file.id,
                transport_element_id: element.id,
                traveler_information_id: file.traveler_information_id,
                trip_id: instruction.trip_id,
                upload_file_name: file.upload_file_name,
                content_type: file.content_type,
                file_path: file.file_path
              });
            });
          });
          return acc;
        }, {} as UploadFileData);
        setUploadFileData(newUploadFileData);

        const queryStr = _.map(params, (value, key) => `${key}=${value}`).join('&');
        history.push(`/arrangement/todo_list?tab=${tab}&${queryStr}`);

        // 範囲外ページ補正。変更がなければ同一オブジェクトを返し、再fetchさせない
        setCommittedQuery(prev =>
          prev && prev.page > 1 && res.total_page < prev.page ? { ...prev, page: 1 } : prev
        );
      })
      .catch(e => {
        if (e instanceof Error && e.name === 'AbortError') return;
        if (requestId !== requestIdRef.current) return;

        if (e instanceof HTTPError && e.response?.data.errors) {
          const errorMsg =
            typeof e.response.data.errors === 'string'
              ? e.response.data.errors
              : JSON.stringify(e.response.data.errors);
          alert(errorMsg);

          const lastSuccessfulQuery = lastSuccessfulQueryRef.current;
          if (lastSuccessfulQuery) {
            // フォーム入力と実行済み条件を直前の成功時の値へ巻き戻す
            setOrganizationIds?.(
              lastSuccessfulQuery.organizationIds
                ? lastSuccessfulQuery.organizationIds.split(',').map(id => parseInt(id, 10))
                : []
            );
            setPreferredTicketingMethod(lastSuccessfulQuery.preferredTicketingMethod);
            setOnlyPremiumSupport(lastSuccessfulQuery.onlyPremiumSupport);
            setTicketingMethod(lastSuccessfulQuery.ticketingMethod);
            setSortKey(lastSuccessfulQuery.sortKey);
            setSortDirection(lastSuccessfulQuery.sortDirection);
            setStartDateFrom(lastSuccessfulQuery.startDateFrom);
            setStartDateTo(lastSuccessfulQuery.startDateTo);
            setReceivedDatetimeFrom(lastSuccessfulQuery.receivedDatetimeFrom);
            setReceivedDatetimeTo(lastSuccessfulQuery.receivedDatetimeTo);
            setTripIdLeadingDigits(lastSuccessfulQuery.tripIdLeadingDigits);
            setOnlyOthers(lastSuccessfulQuery.onlyOthers);
            setAutoReservationStatuses(lastSuccessfulQuery.autoReservationStatuses);
            setCommittedQuery(prev => (_.isEqual(prev, lastSuccessfulQuery) ? prev : lastSuccessfulQuery));
          } else {
            // 初回検索が失敗した場合は未検索状態に戻す（フォーム入力は保持する）
            setCommittedQuery(null);
          }
        } else {
          throw e;
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [committedQuery, history, setOrganizationIds]);

  const executeSearch = () => {
    setCommittedQuery({
      page: 1,
      organizationIds: organizationIds.join(','),
      preferredTicketingMethod,
      onlyPremiumSupport,
      ticketingMethod,
      sortKey,
      sortDirection,
      startDateFrom,
      startDateTo,
      receivedDatetimeFrom,
      receivedDatetimeTo,
      tripIdLeadingDigits,
      onlyOthers,
      autoReservationStatuses
    });
  };

  const handlePageChange = (newPage: number) => {
    setCommittedQuery(prev => (prev ? { ...prev, page: newPage } : prev));
  };

  const handleSortKeyChange = (key: SortKey) => {
    setSortKey(key);
    setCommittedQuery(prev => (prev ? { ...prev, sortKey: key, page: 1 } : prev));
  };

  const handleSortDirectionChange = (direction: SortDirection) => {
    setSortDirection(direction);
    setCommittedQuery(prev => (prev ? { ...prev, sortDirection: direction, page: 1 } : prev));
  };

  // 自動読み込みを廃止したため、タブを開いた時点でURLにタブ情報を反映する
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== tab) {
      params.set('tab', tab);
      history.replace(`/arrangement/todo_list?${params.toString()}`);
    }
  }, [history]);

  const handleSendTicketing = (ti: TicketingInstruction) => {
    const hasEmptyReservationNumber = ti.reservationNumbers.some(
      num => num === null || num === undefined || num.trim() === ''
    );
    if (hasEmptyReservationNumber) {
      Notification.error('予約番号が空欄です');
      return;
    }

    const isQrMethod = ti.ticketingMethod != null && ['qr_east', 'qr_west'].includes(ti.ticketingMethod);
    const hasTravelerWithoutAttachFile = ti.travelerInformations?.some(traveler => {
      const travelerIdKey = traveler.id?.toString() || 'null';
      return (
        !uploadFileData[ti.traceId] ||
        Object.values(uploadFileData[ti.traceId]).every(
          filesByTraveler => !filesByTraveler[travelerIdKey] || filesByTraveler[travelerIdKey].length === 0
        )
      );
    });

    const hasAttachedFiles =
      !!uploadFileData[ti.traceId] &&
      Object.values(uploadFileData[ti.traceId])
        .flat()
        .some(file => file && typeof file === 'object' && Object.keys(file).length > 0);

    if (hasAttachedFiles) {
      setSelectedTicketingInstruction(ti);
      setIsConfirmModalOpen(true);
      return;
    }

    if (isQrMethod && hasTravelerWithoutAttachFile) {
      handleQrConfirmModal(ti);
      return;
    }

    sendTicketing(ti);
  };

  const handleQrConfirmModal = async (ti: TicketingInstruction) => {
    const { result } = await openConfirmModal({
      description: 'ファイルが添付されていませんが、このまま発券完了しますか？',
      submitButtonLabel: '発券完了'
    });

    if (result) {
      sendTicketing(ti);
    }
  };

  const sendTicketing = useCallback(
    (ti: TicketingInstruction) => {
      setIsUpdating(true);
      setError(null);

      const fare = fares.get(ti.orderItemId);
      const params = {
        trace_id: ti.traceId,
        reservation_numbers: ti.reservationNumbers,
        upload_file_data: uploadFileData[ti.traceId] || {},
        ...(fare !== undefined ? { fare } : {})
      };

      const hasUploadedFiles =
        !!uploadFileData[ti.traceId] &&
        Object.values(uploadFileData[ti.traceId])
          .flat()
          .some(file => file && typeof file === 'object' && Object.keys(file).length > 0);

      Fetcher.put<void>('/arrangement/ticketings/proceed.json', params)
        .then(() => {
          if (hasUploadedFiles) {
            Notification.success('アップロードが完了しました');
          }
          setProcessedOrderItems(prev => {
            const next = new Map(prev);
            next.set(ti.orderItemId, 'proceeded');
            return next;
          });
          // 一覧は再取得せず件数表示のみ更新する（再fetch時はサーバの最新値で上書きされる）
          setTotalCount(prev => Math.max(prev - 1, 0));
        })
        .catch(e => {
          setError({
            traceId: ti.traceId,
            message:
              e instanceof HTTPError && e.response?.data.errors
                ? e.response.data.errors.join('\n')
                : 'エラーが発生しました'
          });
        })
        .finally(() => {
          setIsUpdating(false);
        });
    },
    [fares, uploadFileData]
  );

  const revertTicketingInstruction = useCallback((ti: TicketingInstruction) => {
    setIsUpdating(true);
    setError(null);
    const params = {
      order_item_id: ti.orderItemId
    };
    Fetcher.put<void>('/arrangement/ticketing_instructions/revert.json', params)
      .then(() => {
        setProcessedOrderItems(prev => {
          const next = new Map(prev);
          next.set(ti.orderItemId, 'reverted');
          return next;
        });
        // 一覧は再取得せず件数表示のみ更新する（再fetch時はサーバの最新値で上書きされる）
        setTotalCount(prev => Math.max(prev - 1, 0));
      })
      .catch(e => {
        setError({
          traceId: ti.traceId,
          message:
            e instanceof HTTPError && e.response?.data.errors
              ? e.response.data.errors.join('\n')
              : 'エラーが発生しました'
        });
      })
      .finally(() => setIsUpdating(false));
  }, []);

  const isConvertWordToBold = (message: string): boolean => {
    return message.startsWith('座席：') && message.startsWith('座席：指定席') === false;
  };

  const handleUploadFileChange = (
    file: File,
    traceId: string,
    transportElementId: number,
    travelerInformationId: string | null
  ) => {
    if (!file) return;
    TransportElementAttachFile.convertFileToBase64(file)
      .then(base64String => {
        const newFileData = {
          upload_file_name: file.name,
          content_type: file.type,
          traveler_information_id: travelerInformationId,
          upload_file_base64: base64String
        };

        setUploadFileData(prev => {
          const travelerIdKey = travelerInformationId ?? 'null';
          const updated = { ...prev };
          if (!updated[traceId]) {
            updated[traceId] = {};
          }
          if (!updated[traceId][transportElementId]) {
            updated[traceId][transportElementId] = {};
          }
          if (!updated[traceId][transportElementId][travelerIdKey]) {
            updated[traceId][transportElementId][travelerIdKey] = [];
          }
          if (!updated[traceId][transportElementId]) {
            updated[traceId][transportElementId] = {};
          }
          updated[traceId][transportElementId][travelerIdKey].push(newFileData);
          return updated;
        });
      })
      .catch(error => {
        console.error('Failed to convert file to Base64', error);
      });
  };

  const handleDeleteFile = (
    traceId: string,
    transportElementId: number,
    travelerInformationId: string | null,
    index: number
  ) => {
    setUploadFileData(prev => {
      const travelerIdKey = travelerInformationId ?? 'null';
      const updated = { ...prev };

      if (updated[traceId]?.[transportElementId]?.[travelerIdKey]?.length > index) {
        updated[traceId][transportElementId][travelerIdKey].splice(index, 1);

        if (updated[traceId][transportElementId][travelerIdKey].length === 0) {
          delete updated[traceId][transportElementId][travelerIdKey];
        }
        if (Object.keys(updated[traceId][transportElementId]).length === 0) {
          delete updated[traceId][transportElementId];
        }
        if (Object.keys(updated[traceId]).length === 0) {
          delete updated[traceId];
        }
      }
      return updated;
    });
  };

  const handleStartDateFromChange = (date: moment.Moment | null) => {
    setStartDateFrom(date ? date.format('YYYY-MM-DD') : null);
  };

  const handleStartDateToChange = (date: moment.Moment | null) => {
    setStartDateTo(date ? date.format('YYYY-MM-DD') : null);
  };

  const handleReceivedDatetimeFromChange = (date: moment.Moment | null) => {
    setReceivedDatetimeFrom(date ? date.second(0).format('YYYY-MM-DD HH:mm:ss') : null);
  };

  const handleReceivedDatetimeToChange = (date: moment.Moment | null) => {
    setReceivedDatetimeTo(date ? date.second(59).format('YYYY-MM-DD HH:mm:ss') : null);
  };

  const getAttachedFiles = (ti: TicketingInstruction) => {
    const files: Array<{
      fileName: string;
      contentType: string;
      base64?: string;
      filePath?: string;
      travelerName?: string;
    }> = [];

    if (uploadFileData[ti.traceId]) {
      Object.entries(uploadFileData[ti.traceId]).forEach(([transportElementId, filesByTraveler]) => {
        Object.entries(filesByTraveler).forEach(([travelerIdKey, fileList]) => {
          fileList.forEach(file => {
            let travelerName = '未指定';

            if (travelerIdKey === 'null') {
              if (ti.travelerInformations && ti.travelerInformations.length > 0) {
                travelerName = ti.travelerInformations[0].name || '未指定';
              }
            } else {
              const traveler = ti.travelerInformations?.find(t => t.id?.toString() === travelerIdKey);
              if (traveler) {
                travelerName = traveler.name || '未指定';
              }
            }

            files.push({
              fileName: file.upload_file_name,
              contentType: file.content_type,
              base64: 'upload_file_base64' in file ? file.upload_file_base64 : undefined,
              filePath: 'file_path' in file ? file.file_path : undefined,
              travelerName
            });
          });
        });
      });
    }

    return files;
  };

  const renderPdfPreview = (file: {
    fileName: string;
    contentType: string;
    base64?: string;
    filePath?: string;
  }) => {
    if (!file.contentType.includes('pdf')) {
      return <div>PDFファイルではありません</div>;
    }

    if (file.base64) {
      return (
        <iframe
          src={`data:${file.contentType};base64,${file.base64}`}
          style={{
            width: '600px',
            height: '800px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
          title={file.fileName}
        />
      );
    }

    if (file.filePath) {
      return (
        <iframe
          src={`/arrangement/ticketings/download_file?file_path=${encodeURIComponent(file.filePath)}`}
          style={{
            width: '600px',
            height: '800px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
          title={file.fileName}
        />
      );
    }

    return <div>ファイルの表示に失敗しました</div>;
  };

  return (
    <>
      <SearchArea>
        <SearchBlock>
          <SearchLabel>法人</SearchLabel>
          {renderOrganizationSelector()}
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>旅程の発券方法</SearchLabel>
          <Select
            value={preferredTicketingMethod}
            onChange={e =>
              setPreferredTicketingMethod(e.target.value as keyof typeof PreferredTicketingMethodQuery)
            }
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {PreferredTicketingMethodQueryKeys.map(key => (
              <option key={key} value={key}>
                {PreferredTicketingMethodQuery[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>発券方法</SearchLabel>
          <Select
            value={ticketingMethod}
            onChange={e => setTicketingMethod(e.target.value as TicketingMethodKey2)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {TicketingMethodKeys2.map(key => (
              <option key={key} value={key}>
                {TicketingMethod2[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>自動予約ステータス</SearchLabel>
          <AutoReservationStatusSelector
            selectedStatuses={autoReservationStatuses}
            onChange={setAutoReservationStatuses}
          />
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyPremiumSupport} onChange={e => setOnlyPremiumSupport(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>土日祝オプション企業のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>出発日</SearchLabel>
          <Box display="flex" alignItems="center">
            <Datepicker
              value={startDateFrom ? new Date(startDateFrom) : undefined}
              onChange={date => handleStartDateFromChange(date ? moment(date) : null)}
              placeholder="開始日"
              fullWidth
            />
            <Box mx={1}>〜</Box>
            <Datepicker
              value={startDateTo ? new Date(startDateTo) : undefined}
              onChange={date => handleStartDateToChange(date ? moment(date) : null)}
              placeholder="終了日"
              fullWidth
            />
          </Box>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>依頼日</SearchLabel>
          <Box display="flex" alignItems="center">
            <Datepicker
              type="datetime-local"
              value={receivedDatetimeFrom ? new Date(receivedDatetimeFrom) : undefined}
              onChange={date => handleReceivedDatetimeFromChange(date ? moment(date) : null)}
              placeholder="開始日時"
              fullWidth
            />
            <Box mx={1}>〜</Box>
            <Datepicker
              type="datetime-local"
              value={receivedDatetimeTo ? new Date(receivedDatetimeTo) : undefined}
              onChange={date => handleReceivedDatetimeToChange(date ? moment(date) : null)}
              placeholder="終了日時"
              fullWidth
            />
          </Box>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>並び順</SearchLabel>
          <select
            value={sortKey}
            onChange={e => handleSortKeyChange(e.target.value as SortKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            <option value="organization">法人</option>
            <option value="received_at">依頼日</option>
            <option value="started_at">出発日</option>
          </select>
          <Radio
            checked={sortDirection === 'asc'}
            onChange={() => handleSortDirectionChange('asc')}
            style={{ marginRight: '5px' }}
          >
            昇順▲
          </Radio>
          <Radio checked={sortDirection === 'desc'} onChange={() => handleSortDirectionChange('desc')}>
            降順▼
          </Radio>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>Trip ID上一桁</SearchLabel>
          <Box display="flex" flexWrap="wrap">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
              <Checkbox
                key={digit}
                checked={tripIdLeadingDigits.includes(digit)}
                onChange={e => {
                  if (e.target.checked) {
                    setTripIdLeadingDigits(prev => [...prev, digit].sort((a, b) => a - b));
                  } else {
                    setTripIdLeadingDigits(prev => prev.filter(d => d !== digit));
                  }
                }}
                style={{ marginRight: '4px' }}
              >
                {digit}
              </Checkbox>
            ))}
          </Box>
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyOthers} onChange={e => setOnlyOthers(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>ステータス最終更新者が自分以外</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Button onClick={executeSearch}>検索</Button>
        </SearchBlock>
      </SearchArea>
      {committedQuery === null ? (
        <Box padding="20px" textAlign="center" color="gray">
          条件を入力して検索してください
        </Box>
      ) : isLoading ? (
        <Loading />
      ) : (
        <>
          <Box>未処理件数: {totalCount}件</Box>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>出発日</TableCell>
                <TableCell>依頼日</TableCell>
                <TableCell>企業名</TableCell>
                <TableCell>旅程の発券方法</TableCell>
                <TableCell>土日祝対応</TableCell>
                <TableCell>発券方法</TableCell>
                <TableCell>チャットメッセージ</TableCell>
                <TableCell>TripID</TableCell>
                <TableCell>TraceID</TableCell>
                <TableCell>最新のログメッセージ</TableCell>
                <TableCell>内容</TableCell>
                <TableCell>予約番号</TableCell>
                <TableCell>料金</TableCell>
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketingInstcutions.map(ti => (
                <TableRow
                  key={ti.orderItemId}
                  style={
                    processedOrderItems.has(ti.orderItemId)
                      ? { backgroundColor: '#f0f0f0', opacity: 0.6 }
                      : undefined
                  }
                >
                  <TableCell nowrap>{ti.startDate?.format('YYYY-MM-DD') || ''}</TableCell>
                  <TableCell nowrap>{ti.receivedAt.format('YYYY-MM-DD HH:mm')}</TableCell>
                  <TableCell>{ti.organizationName}</TableCell>
                  <TableCell>
                    {ti.preferredTicketingMethod
                      ? PreferredTicketingMethodLabel[ti.preferredTicketingMethod]
                      : PreferredTicketingMethodLabel.unset}
                  </TableCell>
                  <TableCell>{ti.premiumSupport ? '◯' : ''}</TableCell>
                  <TableCell>{ti.ticketingMethod ? TicketingMethod[ti.ticketingMethod] : ''}</TableCell>
                  <TableCell>
                    {ti.latestMessage?.split('\n').map((line, index) => (
                      <Box key={index}>{line}</Box>
                    ))}
                  </TableCell>
                  <TableCell nowrap>
                    <Link
                      href={`/arrangement/virtual_counter?trip_id=${ti.tripId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ti.tripId}
                    </Link>
                  </TableCell>
                  <TableCell nowrap>{ti.traceId}</TableCell>
                  <TableCell>
                    {ti.latestLog && <span>{ti.latestLog}</span>}
                    <p>
                      <Link onClick={() => setLogModalTraceId(ti.traceId)}>ログ</Link>)
                    </p>
                  </TableCell>{' '}
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {ti.content?.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {isConvertWordToBold(line) ? <strong>{line}</strong> : <span>{line}</span>}
                        {index !== (ti.content?.split('\n').length || 0) - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </TableCell>
                  <TableCell style={{ minWidth: '250px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    <>
                      {ti.reservationNumbers.map((reservationNumber, i) => {
                        const isTraceIdMatching = isMatchingTraceId(reservationNumber || '', ti.traceId);
                        return (
                          <Box key={i}>
                            <Input
                              value={reservationNumber || ''}
                              onChange={e => {
                                ti.reservationNumbers[i] = e.target.value;
                              }}
                              style={{
                                backgroundColor: isTraceIdMatching ? '#ffebee' : undefined,
                                borderColor: isTraceIdMatching ? '#f8bbd9' : undefined
                              }}
                            />
                          </Box>
                        );
                      })}
                      {ti.ticketingMethod != null &&
                        ['qr_west', 'qr_east'].includes(ti.ticketingMethod) &&
                        ti?.transportElements?.map((element, j) => (
                          <Box key={j} padding="5px">
                            <TransportElementAttachFileForm
                              attachFiles={element.transportElementAttachFiles}
                              travelerInformations={ti?.travelerInformations || []}
                              attachableType={element.type}
                              attachableId={element.id}
                              onChange={(file, transportElementId, travelerInformationId) =>
                                handleUploadFileChange(
                                  file,
                                  String(ti.traceId),
                                  element.id || 0,
                                  travelerInformationId
                                )
                              }
                              onDelete={(transportElementId, travelerInformationId, index) =>
                                handleDeleteFile(String(ti.traceId), element.id || 0, travelerInformationId, index)
                              }
                            />
                          </Box>
                        ))}
                    </>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      inputMode="numeric"
                      value={
                        fares.has(ti.orderItemId)
                          ? String(fares.get(ti.orderItemId))
                          : ti.price !== null && ti.price !== undefined
                          ? String(ti.price)
                          : ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) {
                          const num = val === '' ? undefined : parseInt(val, 10);
                          setFares(prev => {
                            const next = new Map(prev);
                            if (num === undefined) {
                              next.delete(ti.orderItemId);
                            } else {
                              next.set(ti.orderItemId, num);
                            }
                            return next;
                          });
                        }
                      }}
                      disabled={processedOrderItems.has(ti.orderItemId)}
                      style={{ width: '100px' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Button
                        loading={isUpdating}
                        onClick={() => handleSendTicketing(ti)}
                        disabled={processedOrderItems.has(ti.orderItemId)}
                      >
                        発券完了
                      </Button>
                    </Box>
                    <Box marginTop="10px">
                      <Button
                        loading={isUpdating}
                        color="sub"
                        onClick={() => revertTicketingInstruction(ti)}
                        disabled={processedOrderItems.has(ti.orderItemId)}
                      >
                        発券指示に戻す
                      </Button>
                    </Box>
                    {processedOrderItems.has(ti.orderItemId) && (
                      <Box marginTop="10px">
                        {processedOrderItems.get(ti.orderItemId) === 'proceeded'
                          ? '発券完了済'
                          : '発券指示に戻し済'}
                      </Box>
                    )}
                    {error?.traceId === ti.traceId && !processedOrderItems.has(ti.orderItemId) && (
                      <Box marginTop="10px" color="red">
                        {error.message}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* 発券確認モーダル */}
          <Modal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} size="large">
            <ModalHeader>発券内容確認</ModalHeader>
            <ModalBody>
              {selectedTicketingInstruction && (
                <Box>
                  {/* 内容 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      内容
                    </Box>
                    <Box>
                      {selectedTicketingInstruction.content?.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {isConvertWordToBold(line) ? <strong>{line}</strong> : <>{line}</>}
                          {index !== (selectedTicketingInstruction.content?.split('\n').length || 0) - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>

                  {/* 予約番号 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      予約番号
                    </Box>
                    <Box>
                      {selectedTicketingInstruction.reservationNumbers.map((reservationNumber, i) => (
                        <Box key={i} marginBottom="5px">
                          {reservationNumber || '未入力'}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* 出張者名 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      出張者名
                    </Box>
                    <Box>
                      {selectedTicketingInstruction.travelerInformations &&
                      selectedTicketingInstruction.travelerInformations.length > 0 ? (
                        selectedTicketingInstruction.travelerInformations.map((traveler, i) => (
                          <Box key={i} marginBottom="5px">
                            {traveler.name || '名前未設定'}
                          </Box>
                        ))
                      ) : (
                        <Box marginBottom="5px" color="gray">
                          出張者情報がありません
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* 添付ファイル一覧 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      添付ファイル
                    </Box>
                    {getAttachedFiles(selectedTicketingInstruction).map((file, index) => (
                      <Box
                        key={index}
                        marginBottom="15px"
                        padding="10px"
                        border="1px solid #eee"
                        borderRadius="4px"
                      >
                        <Box fontWeight="bold" marginBottom="5px">
                          {file.fileName} ({file.travelerName})
                        </Box>
                        {file.contentType.includes('pdf') && <Box marginTop="10px">{renderPdfPreview(file)}</Box>}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="sub" onClick={() => setIsConfirmModalOpen(false)}>
                キャンセル
              </Button>
              <Button
                onClick={() => {
                  if (selectedTicketingInstruction) {
                    sendTicketing(selectedTicketingInstruction);
                  }
                  setIsConfirmModalOpen(false);
                  setSelectedTicketingInstruction(null);
                }}
              >
                確認完了
              </Button>
            </ModalFooter>
          </Modal>

          {/* ログモーダル */}
          {logModalTraceId !== null && (
            <LogModal traceId={logModalTraceId} onClose={() => setLogModalTraceId(null)} />
          )}

          <TablePagination
            count={totalPage}
            page={committedQuery.page}
            onChange={(_event: unknown, newPage: number) => handlePageChange(newPage)}
          />
        </>
      )}
    </>
  );
});

export default Ticketing;
